import React, {useEffect, useState} from 'react';
import ReactDOM from "react-dom/client";
import queryString from 'query-string';
import useFetchData from './fetcher';
import ParagraphFilters from './paragraph-filters';
import TextTruncate from './text-truncate';
import GTMDataLayer from './gtm';

import './scss/incentives-paragraph.scss';


const IncentivesApp = (props) => {
  const langcode = (drupalSettings.path.currentLanguage == 'en') ? '/' : '/' + drupalSettings.path.currentLanguage + '/';
  const fetchUrl = langcode + 'rest-api/cp-incentives-data';
  const [fetch, setFetch] = useState(fetchUrl);
  const [filters, setFilters] = useState({search: '', industry: '', state: ''});
  const [isReady, setIsReady] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Manage incentive or incentives.
  const wordIncentive = (count) => {
    return (count == 1) ? Drupal.t('incentive') : Drupal.t('incentives');
  };

  useEffect(() => {
    // Grab the search and filters from the query string on the first run.
    const init = {search: '', industry: '', state: '', type: ''};
    setIsReady(true);

    // Set state if current region is set and no query provided.
    if (!props.appState && drupalSettings.current_region) {
      init.state = drupalSettings.current_region;
    }

    // Preset industry tags if provided.
    if (props.appIndustry) {
      init.industry = props.appIndustry;
    }

    // Preset industry tags if provided.
    if (props.appType) {
      init.type = props.appType;
    }

    setFilters(init);

    // Track initial page load with GTM.
    const startFilters = Object.assign({}, init);
    if (startFilters.industry == '') {
      startFilters.industry = 'undefined';
    }
    GTMDataLayer({
      event: 'incentives_paragraph_start',
      pageUrl: location.href,
      filters: startFilters
    });
  }, []);

  useEffect(() => {
    // Fetch data from the rest resource.
    if (isReady) {
      useFetchData(fetch, setData, setLoading);
    }
  }, [fetch]);

  // build the fetch url based on search and filters.
  useEffect(() => {
    if (isReady) {
      setFetch(`${fetchUrl}?${queryString.stringify(filters)}&fed-limit=${props.appFedNumber}&state-limit=${props.appStateNumber}&paragraph=1`);
    }
  }, [filters]);

  // Just a wrapper for the state loop.
  const Wrapper = ({ children }) => {
    return <>{ children }</>;
  }

  // Link to main app.
  const incentivesAppLink = `${langcode}incentives?p=1#${queryString.stringify({...filters, limit: '', type: props.appTypePref})}`;

  return (
    <div className='cp-incentives-paragraph-app'>
      {loading && <div>{Drupal.t('Loading...')}</div>}

      {(!loading && data.pagination && data.pagination.total && props.appStateNumber > 0) && (
        <div className='filters'>
          <span>
            {Drupal.t('We found @total @word in', {'@total': data.count.state, '@word': wordIncentive(data.count.state)})}
          </span>
          <ParagraphFilters dataFilters={data.filters}  setFilters={setFilters} filters={filters} isReady={isReady} />
          <span>
            {Drupal.t('and  @total federal @word.', {'@total': data.count.federal, '@word': wordIncentive(data.count.federal)})}
          </span>
        </div>
      )}

      <div className='list'>

        {/* List State Incentives next. */}
        {(!loading && data.incentives && data.filters && props.appStateNumber > 0) && (
          <div className='state-list'>

            {Object.entries(data.filters.state).map(([stateId, state], i) => (
              <Wrapper key={i}>
              {(data.incentives[stateId]) && (
                <>
                  <h3>{state} {Drupal.t('Incentives')}
                    <span className='list-count-available'>
                      {Drupal.t('@numb of @total available', {'@numb': data.incentives[stateId].length,'@total': data.count.state})}
                    </span>
                  </h3>

                  {(data.incentives[stateId].length > 0) ? data.incentives[stateId].map((item, i) => (
                    <div key={i} className="body">
                      <div className='title'>{item.title}</div>
                      <TextTruncate
                        line={3}
                        showMoreLabel='Read more +'
                        textCopy={item.body}
                        previewCopy={item.body_preview}
                        nid={item.nid}
                      />
                    </div>
                  )) : (
                    <div className='no-incentives'>
                      {Drupal.t('No incentives found')}
                    </div>
                  )}
                </>
              )}
              </Wrapper>
            ))}

            <div className='count-total'>
              {(data.count && filters.state && data.incentives[filters.state] && data.count.state > data.incentives[filters.state].length) && (
                <a href={incentivesAppLink}>
                  {Drupal.t('Plus @count more @word', {'@count': data.count.state - data.incentives[filters.state].length, '@word': wordIncentive(data.count.state - data.incentives[filters.state].length)})}
                </a>
                )}
            </div>
          </div>
        )}

        {/* List Federal Incentives first. */}
        {(!loading && data.incentives['federal'] && props.appFedNumber > 0) && (
          <div className='federal-list'>
            <h3>{Drupal.t('Federal Incentives')}
              <span className='list-count-available'>
                {Drupal.t('@numb of @total available', {'@numb': data.incentives['federal'].length,'@total': data.count.federal})}
              </span>
            </h3>

            {(data.incentives['federal'].length > 0) ? data.incentives['federal'].map((item, i) => (
              <div key={i} className="body">
                <div className='title'>{item.title}</div>
                <TextTruncate
                  line={3}
                  showMoreLabel='Read more +'
                  textCopy={item.body}
                  previewCopy={item.body_preview}
                  nid={item.nid}
                />
              </div>
            )) : (
              <div className='no-incentives'>
                {Drupal.t('No incentives found')}
              </div>
            )}

            <div className='count-total'>
              {(data.count && data.count.federal > data.incentives['federal'].length) && (
                <a href={incentivesAppLink}>
                  {Drupal.t('Plus @count more @word', {'@count': data.count.federal - data.incentives['federal'].length, '@word': wordIncentive(data.count.federal - data.incentives['federal'].length)})}
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      <div className='disclaimer'>
        <p>{Drupal.t('ChargePoint is providing information on this tax credit only and is not the issuer of the tax credit. ChargePoint is not responsible for any issues that may result from your tax credit, including but not limited to: invalid tax credit submission, lengthy processing times or delays in processing or receiving your tax credit, any other issues pertaining to your tax credit.')}</p>
      </div>

      {/* Link to incentives main app */}
      {(!loading && data.pagination && data.pagination.total) && (
        <div className='incentives-link'>
          {(props.appFooterText) && (<h3>{props.appFooterText}</h3>)}
          <a href={incentivesAppLink} className='driverButton'>{props.appButtonText}</a>
        </div>
      )}
    </div>
  );
};


const appRoot = document.querySelectorAll('.cp-incentives-paragraph');
appRoot.forEach((element) => {
  const root = ReactDOM.createRoot(element);
  root.render(
    <React.StrictMode>
      <IncentivesApp 
        appType={element.getAttribute('data-incentives-type')}
        appTypePref={element.getAttribute('data-incentives-type-pref')}
        appIndustry={element.getAttribute('data-incentive-industry')}
        appFedNumber={element.getAttribute('data-incentives-number-fed')}
        appStateNumber={element.getAttribute('data-incentives-number-state')}
        appFooterText={element.getAttribute('data-incentive-footer-text')}
        appButtonText={element.getAttribute('data-incentive-button-text')} />
    </React.StrictMode>
  );
});
